import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwSecurityUserService, DwCacheService, DwEventService, DwConfigService, DwConfigServiceToken, sortArrayByField, DwRoutingService } from "@devwareapps/devware-cap";
import { Observable, of } from "rxjs";
import { QuizEntity, AppMetaDataItemNames, FlightSchoolGroupEntity, FlightSchoolGroupWithStudentCourseDetailQueryEntity, AppMetaData, StudentCourseEntity, PackageEntity, InstructorEntity, DocumentGroupEntity, DocumentGroupAllItems, DocumentFolderEntity, DocumentItemEntity, DocumentItemStatusAllItems, DocumentOwnerTypeAllItems } from "../../../meta-data/app-meta-data.service";
import { map, mergeMap, tap } from "rxjs/operators";
import { HttpClient } from "@angular/common/http";
import { DocumentCardData, DocumentCardType } from "../models/document-card-data.model";
import { DocumentDataUpdateInfo } from "../models/docuemnt-data-update-info.model";
import { AdminManagerService } from "../../admin/services/admin-manager.service";
import { AppPermissions } from "../../../meta-data/app-permissions.enum";
import { DocumentsRepositoryService } from "./documents-repository.service";



@Injectable({ providedIn: 'root' })
export class DocumentsManagerService {


    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) private configService: DwConfigService,


        private adminManagerService: AdminManagerService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private dwEventService: DwEventService,
        private router: Router,
        private dwRoutingService: DwRoutingService,
        private http: HttpClient
    ) { }

    navigateToDocument(card: DocumentCardData, allDocumentCards: DocumentCardData[]) {
        let url: string;

        if (card.documentCardType === DocumentCardType.folder && card.folderItem) {
            // determine current folder based on the path
            url = this.buildFolderPath(card);
        }
        else if (card.documentCardType === DocumentCardType.documentItem && card.documentItem) {
            const folderCard = allDocumentCards.find(dc => dc.documentCardType === DocumentCardType.folder && dc.folderItem?.DocumentFolderId === card.documentItem.ParentDocumentFolderId);
            url = this.buildViewDocumentPath(card, folderCard?.folderPath);
        }

        if (url) {
            this.dwRoutingService.navigateToUrl(url);
        }
    }


    canEditFolder(documentGroup: DocumentGroupEntity, parentFolder: DocumentFolderEntity): boolean {
        if (this.canManageAllDocuments()) {
            return true;
        }

        if (!documentGroup?.AllowSchoolDocuments || !this.canManageSchoolDocuments()) {
            return false;
        }

        // Check if folder belongs to user's flight school
        const aviatorUser = this.adminManagerService.getAviatorUser();

        if (parentFolder == null) {
            return true;
        }

        return parentFolder.DocumentOwnershipTypeId == DocumentOwnerTypeAllItems.FlightSchool && parentFolder.FlightSchoolId == aviatorUser.FlightSchoolId;
    }


    canEditDocument(data: DocumentCardData, documentGroup: DocumentGroupEntity, parentFolder: DocumentFolderEntity): boolean {

        // If you can't edit the folder, you can't edit the document
        if (!this.canEditFolder(documentGroup, parentFolder)) {
            return false;
        }

        // LMS Admins can edit any document or folder
        if (this.canManageAllDocuments()) {
            return true;
        }

        const aviatorUser = this.adminManagerService.getAviatorUser();

        // Flight school admins can only edit documents owned by their flight school
        switch (data.documentCardType) {
            case DocumentCardType.documentItem:
                return data.documentItem?.DocumentOwnershipTypeId == DocumentOwnerTypeAllItems.FlightSchool && data.documentItem.FlightSchoolId == aviatorUser.FlightSchoolId;
            case DocumentCardType.folder:
                return data.folderItem?.DocumentOwnershipTypeId == DocumentOwnerTypeAllItems.FlightSchool && data.folderItem.FlightSchoolId == aviatorUser.FlightSchoolId;
        }

        return false;
    }

    private canManageAllDocuments(): boolean {
        return this.dwSecurityUserService.hasPermission(AppPermissions.lMSDocumentsCanManageAll)
            || this.dwSecurityUserService.hasPermission(AppPermissions.lMSAllSchoolsAdmin);
    }

    private canManageSchoolDocuments(): boolean {
        return this.dwSecurityUserService.hasPermission(AppPermissions.flightSchoolCanManageDocuments)
            || this.dwSecurityUserService.hasPermission(AppPermissions.flightSchoolAdmin);
    }

    determineDocumentGroup(): number {
        if (this.router.url.includes('/protools') || this.router.url.includes('/pro-tools')) {
            return DocumentGroupAllItems.ProTools
        }

        return DocumentGroupAllItems.Documents;
    }

    isViewingDocument(): boolean {
        if (this.router.url.includes('/view/')) {
            return true;
        }
        return false;
    }

    getDocumentItemIdFromUrl(): number {
        const urlParts = this.router.url.split('/');

        return urlParts[urlParts.length - 1] ? parseInt(urlParts[urlParts.length - 1], 10) : null;
    }

    buildFolderPath(documentCard?: DocumentCardData) {
        let baseUrl = '/resources/documents';

        switch (this.determineDocumentGroup()) {
            case DocumentGroupAllItems.ProTools:
                baseUrl = '/resources/pro-tools';
        }

        if (!documentCard || !documentCard?.folderPath) {
            return baseUrl;
        }

        return `${baseUrl}/${documentCard.folderPath}`;
    }

    buildViewDocumentPath(documentCard?: DocumentCardData, folderPath?: string) {
        let baseUrl = '/resources/documents';

        switch (this.determineDocumentGroup()) {
            case DocumentGroupAllItems.ProTools:
                baseUrl = '/resources/pro-tools';
        }

        let url = `${baseUrl}/view/${documentCard.documentItem.DocumentItemId}`;
        if (folderPath) {
            url = `${baseUrl}/${folderPath}/view/${documentCard.documentItem.DocumentItemId}`;
        }

        if (documentCard?.initialPageNumber && documentCard.initialPageNumber > 0) {
            url = `${url}?page=${documentCard.initialPageNumber}`;
        }

        return url;
    }

    determineCurrentFolder(allDocumentCards: DocumentCardData[]): DocumentFolderEntity {
        if (!allDocumentCards) {
            return null;
        }

        const baseUrl = this.buildFolderPath();

        const currentUrl = this.router.url;

        let urlParts = this.router.url.replace(baseUrl, '').trim().split('/');

        let urlPart = urlParts[urlParts.length - 1];

        if (!urlPart) {
            return null;
        }

        const currentFolder = allDocumentCards.find(dc => dc.documentCardType === DocumentCardType.folder && dc.folderKey === urlPart);

        if (currentFolder?.folderItem) {
            return currentFolder.folderItem;
        }

        return null;
    }

    getCurrentDocumentCards(allDocumentCards: DocumentCardData[], documentFolderId: number): DocumentCardData[] {
        return allDocumentCards.filter(dc => (!documentFolderId && !dc.parentFolderId) || dc.parentFolderId == documentFolderId);
    }

    handleDocumentDataUpdate(dataUpdate: DocumentDataUpdateInfo, documentGroup: DocumentGroupEntity): DocumentCardData[] {
        const docItemUpdate = dataUpdate.data?._itemName == AppMetaDataItemNames.DocumentItem;

        // Update the item or folder in the document group, then rebuild the document cards
        if (docItemUpdate) {
            const updatedDocItem = dataUpdate.data as DocumentItemEntity;

            //remove existing item
            const index = documentGroup.DocumentItem.findIndex(existingDocItem => existingDocItem.DocumentItemId === updatedDocItem.DocumentItemId);
            if (index > -1) {
                documentGroup.DocumentItem.splice(index, 1);
            }

            documentGroup.DocumentItem.push(updatedDocItem);
        } else {
            const updatedFolderItem = dataUpdate.data as DocumentFolderEntity;

            //remove existing folder
            const index = documentGroup.DocumentFolder.findIndex(existingFolderItem => existingFolderItem.DocumentFolderId === updatedFolderItem.DocumentFolderId);
            if (index > -1) {
                documentGroup.DocumentFolder.splice(index, 1);
            }
            documentGroup.DocumentFolder.push(updatedFolderItem);
        }

        return this.buildAllDocumentCards(documentGroup);
    }

    buildAllDocumentCards(documentGroup: DocumentGroupEntity): DocumentCardData[] {
        let documentCards: DocumentCardData[] = [];

        for (const documentFolder of documentGroup.DocumentFolder) {
            const documentCard: DocumentCardData = {
                groupOrder: 1,
                uniqueKey: `folder_${documentFolder.DocumentFolderId}`,
                documentCardType: DocumentCardType.folder,
                name: documentFolder.FolderName,
                folderItem: documentFolder,
                parentFolderId: documentFolder.ParentDocumentFolderId,
                folderKey: this.buildFolderKey(documentGroup, documentFolder, documentFolder.ParentDocumentFolderId)
            };

            if (documentFolder.DocumentItemStatusId == DocumentItemStatusAllItems.Active) {
                documentCards.push(documentCard);
            }
        }

        // Build full paths for each folder
        this.buildFolderPaths(documentCards);

        for (const documentItem of documentGroup.DocumentItem) {
            const documentCard: DocumentCardData = {
                groupOrder: 2,
                uniqueKey: `document_${documentItem.DocumentItemId}`,
                documentCardType: DocumentCardType.documentItem,
                documentItem: documentItem,
                name: documentItem.DocumentItemName,
                parentFolderId: documentItem.ParentDocumentFolderId
            };
            if (documentItem.DocumentItemStatusId == DocumentItemStatusAllItems.Active) {
                documentCards.push(documentCard);
            }
        }

        // sort cards by folders first, then by name
        documentCards = this.sortByDocumentCards(documentCards);

        return documentCards;
    }


    private sortByDocumentCards(items: DocumentCardData[]): DocumentCardData[] {
        return items.sort((a, b) => {
            // 1️⃣ sort by groupOrder
            if (a.groupOrder !== b.groupOrder) {
                return a.groupOrder - b.groupOrder;
            }

            // 2️⃣ if groupOrder is the same, sort by name
            return a.name.localeCompare(b.name);
        });
    }

    private buildFolderPaths(documentCards: DocumentCardData[], parentFolderId?: number, parentPath: string = '') {
        const childFolders = documentCards.filter(dc => dc.documentCardType === DocumentCardType.folder && ((!parentFolderId && !dc.parentFolderId) || dc.parentFolderId === parentFolderId));

        for (const childFolder of childFolders) {
            childFolder.folderPath = parentPath ? `${parentPath}/${childFolder.folderKey}` : childFolder.folderKey;

            this.buildFolderPaths(documentCards, childFolder.folderItem?.DocumentFolderId, childFolder.folderPath);
        }
    }

    private buildFolderKey(documentGroup: DocumentGroupEntity, folder: DocumentFolderEntity, currentFolderId?: number) {

        let folderKey = folder.FolderName.replace(/ /g, '_');

        // Check if it's unique
        const matchingFolders = documentGroup.DocumentFolder.filter(f => f.FolderName === folder.FolderName && f.ParentDocumentFolderId === currentFolderId);

        if (matchingFolders.length > 1) {
            folderKey += folder.DocumentFolderId;
        }

        return folderKey;
    }

}
